import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toastVariant, useToast } from 'components/ToastProvider';

import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { ACCOUNT } from 'queries/query-keys';
import TeamMemberService from 'services/TeamMemberService';
import {
  primaryTokenQuery,
  secondaryTokenQuery,
  serviceAccountsInfiniteQuery,
} from 'query/service-account';

const fetchApiKeysQuery = async (params) => {
  const res = await TeamMemberService.getApiKeys(params);
  // match the shape required for infinite scroll
  return res?.results
    ? res
    : {
        count: res?.length || 0,
        errors: null,
        next: null,
        previous: null,
        results: res,
      };
};

const deleteApiKeyQuery = (apiKeyId) =>
  TeamMemberService.deleteApiKey(apiKeyId);
const createApiKeyQuery = () => TeamMemberService.createApiKey();

export const useApiKeysReactQuery = (
  { search, ordering, pageSize = 20, employeeId = null },
  callbacks
) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const queryClient = useQueryClient();

  const infiniteQuery = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: [...ACCOUNT.API_KEYS, employeeId, pageSize, ordering, search],
    queryFn: async ({ pageParam = 1 }) => {
      try {
        const res = await fetchApiKeysQuery({
          search,
          ordering,
          pageSize,
          employeeId,
          page: pageParam,
        });
        return res;
      } catch (err) {
        const message =
          err?.response?.data?.[0] ??
          t('There was a problem loading the data, please  try again.');

        showToast({
          message,
          variant: toastVariant.FAILURE,
        });

        return {
          count: 0,
          errors: null,
          next: null,
          previous: null,
          results: [],
        };
      }
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.next
        ? new URL(lastPage.next).searchParams.get('page')
        : undefined;
    },
    getPreviousPageParam: (previousPage) => {
      return previousPage?.prev
        ? new URL(previousPage.prev).searchParams.get('page')
        : undefined;
    },
    placeholderData: keepPreviousData,
    retry: false,
    throwOnError: false,
    staleTime: 0, // so that if the user has multiple browser windows open things stay in synch
  });

  const refetchApiKeysTokens = useCallback(
    (employeeId) => {
      queryClient.invalidateQueries({
        queryKey: ACCOUNT.API_KEYS,
      });
      queryClient.invalidateQueries({
        queryKey: primaryTokenQuery(employeeId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: secondaryTokenQuery(employeeId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: serviceAccountsInfiniteQuery().queryKey,
      });
    },
    [queryClient]
  );

  const deleteApiKeyMutation = useMutation({
    mutationFn: async (apiKeyData) => {
      await deleteApiKeyQuery(apiKeyData.id);
      return apiKeyData;
    },
    onSuccess: (apiKeyData) => {
      refetchApiKeysTokens(apiKeyData.employee?.id);
    },
    onError: (payload) => {
      const message =
        payload?.response?.data?.[0] ??
        t(
          'The Api Key could not be deleted, please refresh your browser and try again.'
        );

      showToast({
        message,
        variant: toastVariant.FAILURE,
      });
    },
  });

  const creatApiKeyMutation = useMutation({
    mutationFn: async (result) => {
      const { data } = await createApiKeyQuery(result);
      callbacks?.created(data);
    },
    onSuccess: () => {
      refetchApiKeysTokens();
    },
    onError: (payload) => {
      const message =
        payload?.response?.data?.[0] ??
        t(
          'The Api Key could not be created, please refresh your browser and try again.'
        );

      showToast({
        message,
        variant: toastVariant.FAILURE,
      });
    },
  });

  return { infiniteQuery, deleteApiKeyMutation, creatApiKeyMutation };
};
